import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";

type KpiCandidate = {
  id: string;
  proposed_name: string;
  score: number;
  rationale?: string;
  source_dataset_id: string;
  proposed_sql?: string;
  suggested_unit?: string;
  suggested_granularity?: string;
  suggested_direction?: string;
  suggested_domain?: string;
  template_id?: string;
  status: "new" | "in_review" | "accepted" | "rejected";
};

function StatusPill({ status }: { status: KpiCandidate["status"] }) {
  const tone = status === "accepted" ? "good"
    : status === "rejected" ? "bad"
    : status === "in_review" ? "warn"
    : "muted";
  return <span className={`status-pill ${tone}`}>{status}</span>;
}

function ScoreBar({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const cls = score >= 0.85 ? "high" : score >= 0.7 ? "mid" : "low";
  return (
    <div className={`score-bar ${cls}`} title={`Confidence: ${pct}%`}>
      <div className="score-fill" style={{ width: `${pct}%` }} />
      <span className="score-label">{pct}%</span>
    </div>
  );
}

export default function KpiCandidatesPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"all" | "new" | "in_review" | "accepted" | "rejected">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const candidates = useQuery({
    queryKey: ["kpi-candidates", effectiveTenantId],
    queryFn: () => api.get<KpiCandidate[]>("/kpi-candidates"),
    refetchInterval: 15_000,
  });

  const promote = useMutation({
    mutationFn: (candId: string) => api.post(`/kpi-candidates/${candId}/promote`, {
      name: "promoted-from-cand",
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kpi-candidates", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["metrics", effectiveTenantId] });
    },
  });

  const filtered = (candidates.data ?? []).filter(c =>
    filter === "all" ? true : c.status === filter
  );

  const grouped = filtered.reduce<Record<string, KpiCandidate[]>>((acc, c) => {
    const d = c.suggested_domain || "andere";
    acc[d] = acc[d] || [];
    acc[d].push(c);
    return acc;
  }, {});

  const selected = filtered.find(c => c.id === selectedId) ?? null;
  const totalCount = candidates.data?.length ?? 0;
  const newCount = (candidates.data ?? []).filter(c => c.status === "new").length;

  return (
    <div className="page kpi-candidates">
      <h1>KPI-Kandidaten</h1>
      <p className="subtitle">
        Der kpi-discovery-Agent hat <strong>{totalCount}</strong> KPI-Vorschläge
        analysiert, davon <strong>{newCount} neu</strong>. Reviewe sie und uebernimm
        die relevanten als Metrics für dein Cockpit (HITL-Approval).
      </p>

      <div className="filter-bar">
        {(["all", "new", "in_review", "accepted", "rejected"] as const).map(f => (
          <button
            key={f}
            className={`filter-pill ${filter === f ? "active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? "alle" : f}{" "}
            <span className="muted small">
              ({f === "all" ? totalCount : (candidates.data ?? []).filter(c => c.status === f).length})
            </span>
          </button>
        ))}
      </div>

      {candidates.isLoading && <p className="muted">Lade Kandidaten…</p>}
      {!candidates.isLoading && totalCount === 0 && (
        <div className="empty">
          <p>Noch keine Kandidaten. Verbinde eine Datenquelle unter <Link to="/app/sources">Quellen</Link> und
          klicke „Sync jetzt" — der Discovery-Agent erzeugt automatisch Vorschläge.</p>
        </div>
      )}

      <div className="candidates-layout">
        <div className="candidates-list">
          {Object.entries(grouped).sort().map(([domain, items]) => (
            <div key={domain} className="cand-group">
              <h3 className="cand-group-hdr">
                {domain} <span className="muted small">({items.length})</span>
              </h3>
              <ul className="metric-list">
                {items
                  .sort((a, b) => b.score - a.score)
                  .map(c => (
                    <li
                      key={c.id}
                      className={`metric-item cand-row ${selectedId === c.id ? "selected" : ""}`}
                      onClick={() => setSelectedId(c.id)}
                    >
                      <div className="cand-row-main">
                        <div className="cand-name">{c.proposed_name}</div>
                        <div className="muted small">
                          {c.suggested_unit ?? "—"} · {c.suggested_granularity ?? "—"} · {c.suggested_direction ?? "neutral"}
                        </div>
                      </div>
                      <div className="cand-row-meta">
                        <ScoreBar score={c.score} />
                        <StatusPill status={c.status} />
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>

        {selected && (
          <aside className="cand-detail">
            <header>
              <h2>{selected.proposed_name}</h2>
              <button className="btn ghost small" onClick={() => setSelectedId(null)}>Schliessen</button>
            </header>
            <div className="cand-detail-body">
              <div className="cand-meta-grid">
                <div><strong>Score:</strong> {(selected.score * 100).toFixed(0)}%</div>
                <div><strong>Domain:</strong> {selected.suggested_domain ?? "—"}</div>
                <div><strong>Unit:</strong> {selected.suggested_unit ?? "—"}</div>
                <div><strong>Granularität:</strong> {selected.suggested_granularity ?? "—"}</div>
                <div><strong>Direction:</strong> {selected.suggested_direction ?? "neutral"}</div>
                <div><strong>Template:</strong> <code>{selected.template_id ?? "—"}</code></div>
                <div><strong>Dataset:</strong> <code>{selected.source_dataset_id}</code></div>
                <div><strong>Status:</strong> <StatusPill status={selected.status} /></div>
              </div>

              {selected.rationale && (
                <div className="cand-rationale">
                  <div className="muted small">RATIONALE</div>
                  <p>{selected.rationale}</p>
                </div>
              )}

              {selected.proposed_sql && (
                <div className="cand-sql">
                  <div className="muted small">VORGESCHLAGENES SQL</div>
                  <pre><code>{selected.proposed_sql}</code></pre>
                </div>
              )}

              <div className="cand-actions">
                <button
                  className="btn primary"
                  onClick={() => promote.mutate(selected.id)}
                  disabled={promote.isPending || selected.status === "accepted"}
                  title="Erzeugt eine neue Metric-Definition + setzt Kandidat-Status accepted"
                >
                  {promote.isPending ? "Promote…" : "Promote zu Metric"}
                </button>
                <button
                  className="btn ghost"
                  disabled={selected.status === "rejected"}
                  title="Reject — wird nicht zu Metric. Audit-Eintrag bleibt."
                >
                  Reject
                </button>
              </div>

              {promote.isSuccess && (
                <p className="success small">
                  Promote ausgefuehrt. Schau in <Link to="/app/metrics">Metrics</Link> nach.
                </p>
              )}
              {promote.error && (
                <p className="error small">
                  Fehler: {(promote.error as { message?: string }).message ?? "unknown"}
                </p>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
